import { formatUnknownError } from "@project/shared";
import MakerJS, { type IChain, type IModel } from "makerjs";
import {
  ExtrudeGeometry,
  type Group,
  LineSegments,
  LoadingManager,
  type Material,
  Matrix4,
  Mesh,
  MeshPhongMaterial,
  type Object3DEventMap,
  type Scene,
  Shape,
  type Texture,
  TextureLoader,
  Vector3,
} from "three";
// import * as ReflectionMap from "../editor3d/ReflectionMap";
import {
  type Collada,
  ColladaLoader,
} from "three/examples/jsm/loaders/ColladaLoader.js";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";

const texLoader = new TextureLoader();

/** Suppress THREE.ColladaLoader Z-UP warning (Three.js #24289). We keep the loader's scene rotation; vertex data stay Z-UP. */
function installColladaZUpWarnFilter(): void {
  const c = console as {
    warn?: (...args: unknown[]) => void;
    __colladaZUpPatched?: boolean;
  };
  if (c.__colladaZUpPatched) return;
  const originalWarn = console.warn;
  c.warn = function patchedWarn(...args: unknown[]) {
    const msg = args.map((a) => String(a)).join(" ");
    if (msg.includes("ColladaLoader") && msg.includes("Z-UP")) return;
    originalWarn.apply(console, args);
  };
  c.__colladaZUpPatched = true;
}
installColladaZUpWarnFilter();

const getGlobalAssetBase = (): string | undefined => {
  const override = (globalThis as Record<string, unknown>)
    .__EMERGE_ASSET_BASE_URL;
  return typeof override === "string" && override.length > 0
    ? override.replace(/\/$/, "")
    : undefined;
};

const getEnvAssetBase = (): string => {
  try {
    const envBase =
      (import.meta as { env?: { MINIO_FQDN?: string } }).env?.MINIO_FQDN ?? "";
    if (typeof envBase === "string" && envBase.length > 0) {
      return `${envBase.replace(/\/$/, "")}/models`;
    }
  } catch {
    // eslint-disable-next-line no-empty
  }
  return "";
};

const resolveAssetBase = (): string => {
  return getGlobalAssetBase() ?? getEnvAssetBase();
};

const buildAssetUrl = (base: string, path: string): string => {
  const normalizedBase = base.replace(/\/$/, "");
  const normalizedPath = path.replace(/^\/+/, "");
  if (!normalizedBase) {
    return normalizedPath;
  }

  // If path already starts with "models/", don't add it again from base
  // This handles cases where the path is already fully qualified
  if (normalizedPath.startsWith("models/")) {
    // Remove /models from base if present, then add the path
    const baseWithoutModels = normalizedBase.replace(/\/models$/, "");
    return `${baseWithoutModels}/${normalizedPath}`;
  }

  return `${normalizedBase}/${normalizedPath}`;
};

export type DEGREE = number;
export type RADIAN = number;

export function clone(
  mesh: Mesh | LineSegments | Scene | Group<Object3DEventMap>,
): Mesh | LineSegments | Scene | Group<Object3DEventMap> {
  return mesh.clone();
}

type MeshCacheEntry = {
  promise?: Promise<Mesh | LineSegments | Scene | Group<Object3DEventMap>>;
  mesh?: Mesh | LineSegments | Scene | Group<Object3DEventMap>;
};

export const ThreeToolsEvents = {
  MODEL_LOADED: "model_loaded",
  ALL_DONE: "alldone",
} as const;

/**
 * Utility class for loading and manipulating Three.js meshes and geometries.
 *
 * @example
 * // Listen for events
 * import { ThreeToolsInstance, ThreeToolsEvents } from './ThreeTools';
 *
 * ThreeToolsInstance.addEventListener(ThreeToolsEvents.MODEL_LOADED, () => {
 *   console.log('A model finished loading');
 * });
 *
 * ThreeToolsInstance.addEventListener(ThreeToolsEvents.ALL_DONE, () => {
 *   console.log('All models finished loading');
 * });
 */
export class ThreeTools extends EventTarget {
  public numLoading = 0;
  private meshes: Record<string, MeshCacheEntry> = {};
  private manager: LoadingManager;
  private loaderDAE: ColladaLoader;
  private loader: OBJLoader;
  private readonly loaderSTL: STLLoader;

  constructor() {
    super();
    this.manager = new LoadingManager();
    this.loaderDAE = new ColladaLoader(this.manager);
    this.loader = new OBJLoader(this.manager);
    this.loaderSTL = new STLLoader(this.manager);
  }

  public _rnd(x: number, tol = 1): number {
    const itol = Number.parseInt(tol.toString(), 10);
    const rem = tol - itol;

    if (rem > 0) {
      x /= rem;
    }

    x = Math.round(x * 10 ** itol) / 10 ** itol;

    if (rem > 0) {
      x *= rem;
    }

    return x;
  }

  private loadCollada(url: string): Promise<Collada> {
    return new Promise((resolve, reject) => {
      this.loaderDAE.load(url, resolve, undefined, (err) =>
        reject(err instanceof Error ? err : new Error(formatUnknownError(err))),
      );
    });
  }

  private loadOBJ(url: string): Promise<Group<Object3DEventMap>> {
    return new Promise((resolve, reject) => {
      this.loader.load(url, resolve, undefined, reject);
    });
  }

  private loadSTL(url: string): Promise<Mesh> {
    return new Promise((resolve, reject) => {
      this.loaderSTL.load(
        url,
        (geometry) => {
          const material = new MeshPhongMaterial({ color: 0xffffff });
          const mesh = new Mesh(geometry, material);
          resolve(mesh);
        },
        undefined,
        reject,
      );
    });
  }

  public async loadMesh(options: {
    model: string;
  }): Promise<Mesh | LineSegments | Scene | Group<Object3DEventMap> | null> {
    const { model } = options;

    const cached = this.meshes[model];
    if (cached) {
      if (cached.mesh) return clone(cached.mesh);
      return cached.promise?.then(clone) ?? Promise.resolve(null);
    }

    const record: MeshCacheEntry = {};
    this.meshes[model] = record;
    this.numLoading++;

    record.promise = (async () => {
      let loadedMesh: Mesh | LineSegments | Scene | Group<Object3DEventMap>;
      const assetBase = resolveAssetBase();
      const normalizedModel = model.replace(/^\/+/, "");
      const modelUrl = buildAssetUrl(assetBase, normalizedModel);

      if (model.endsWith(".dae")) {
        const scn = await this.loadCollada(modelUrl);
        loadedMesh = scn.scene;
        // Undo loader's Z-UP→Y-UP rotation so the model stays Z-UP for our pipeline
        loadedMesh.rotation.set(0, 0, 0);
        loadedMesh.quaternion.identity();
      } else if (model.endsWith(".obj")) {
        loadedMesh = await this.loadOBJ(modelUrl);
      } else if (model.endsWith(".stl")) {
        loadedMesh = await this.loadSTL(modelUrl);
      } else {
        const objUrl = buildAssetUrl(assetBase, `${normalizedModel}.obj`);
        const obj = await this.loadOBJ(objUrl);
        let mod = model;
        if (mod.endsWith("a") || mod.endsWith("b")) {
          mod = mod.slice(0, -1);
        }
        const paths = [
          buildAssetUrl(assetBase, `${mod}_full.jpg`),
          buildAssetUrl(assetBase, `${mod}_normal.jpg`),
        ];
        const textures = await Promise.all(
          paths.map(
            (path) =>
              new Promise<Texture | undefined>((resolve) => {
                texLoader.load(path, resolve, undefined, () =>
                  resolve(undefined),
                );
              }),
          ),
        );
        const material = new MeshPhongMaterial({
          map: textures[0],
          normalMap: textures[1],
        });
        let found: Mesh | LineSegments | null = null;
        obj.traverse((child) => {
          if (child instanceof Mesh || child instanceof LineSegments) {
            child.material = material;
            if (!found) {
              found = child;
            }
          }
        });
        loadedMesh = found || obj;
      }
      loadedMesh.userData = {
        model: modelUrl,
        dontPurge: true,
      };

      this.dispatchEvent(new Event(ThreeToolsEvents.MODEL_LOADED));
      record.mesh = loadedMesh;
      return loadedMesh;
    })()
      .catch((error) => {
        console.error("❌ Failed to load mesh:", error);
        throw error;
      })
      .finally(() => {
        this.numLoading--;
        if (this.numLoading === 0) {
          this.dispatchEvent(new Event(ThreeToolsEvents.ALL_DONE));
        }
      });

    return record.promise.then(clone);
  }

  public Chain2Shape(chain: IChain, originX = 0, originY = 0): Shape {
    const result = new Shape();
    result.autoClose = true;
    const points = MakerJS.chain.toKeyPoints(chain, 1);
    const p0 = points[0];
    if (!p0 || p0[0] === undefined || p0[1] === undefined) {
      return result;
    }
    result.moveTo(p0[0] + originX, p0[1] + originY);
    for (let p = 1; p < points.length; p++) {
      const point = points[p];
      if (point && point[0] !== undefined && point[1] !== undefined) {
        result.lineTo(point[0] + originX, point[1] + originY);
      }
    }
    return result;
  }

  public MakerJSPath2Shape(subModel: IModel, originX = 0, originY = 0): Shape {
    const result = new Shape();
    result.autoClose = true;
    if (subModel.origin) {
      originX += subModel.origin[0] ?? 0;
      originY += subModel.origin[1] ?? 0;
    }
    if (subModel.paths) {
      const chain = MakerJS.model.findSingleChain(subModel);
      if (chain) {
        const points = MakerJS.chain.toKeyPoints(chain, 0.1);
        const p0 = points[0];
        if (p0 && p0[0] !== undefined && p0[1] !== undefined) {
          result.moveTo(p0[0] + originX, p0[1] + originY);
        }
        for (let p = 1; p < points.length; p++) {
          const point = points[p];
          if (point && point[0] !== undefined && point[1] !== undefined) {
            result.lineTo(point[0] + originX, point[1] + originY);
          }
        }
      }
      return result;
    }
    return result;
  }

  public MakerJSModel2Shape(model: IModel): Shape {
    let result: Shape | null = null;
    const chains = MakerJS.model.findChains(model) as IChain[];
    for (const chain of chains) {
      if (result === null) {
        result = this.Chain2Shape(chain);
      } else {
        result.holes.push(this.Chain2Shape(chain));
      }
    }
    if (!result) {
      result = this.MakerJSPath2Shape(model);
    }
    return result;
  }

  public trans3(
    mesh: Mesh | LineSegments,
    vec: [number, number, number],
  ): Mesh | LineSegments {
    mesh.applyMatrix4(new Matrix4().makeTranslation(vec[0], vec[1], vec[2]));
    return mesh;
  }

  public DEG2RAD(deg: DEGREE): RADIAN {
    return (deg * Math.PI) / 180;
  }

  public rot3(
    mesh: Mesh | LineSegments,
    axes: string,
    angle: number,
  ): Mesh | LineSegments {
    const v = new Vector3(0, 0, 0);
    for (const axis of axes.split("")) {
      if (axis === "2" || axis.toLowerCase() === "z") {
        v.y = 1;
      }
      if (axis === "0" || axis.toLowerCase() === "x") {
        v.x = 1;
      }
      if (axis === "1" || axis.toLowerCase() === "y") {
        v.z = 1;
      }
    }
    mesh.applyMatrix4(
      new Matrix4().makeRotationAxis(v.normalize(), this.DEG2RAD(angle)),
    );
    return mesh;
  }

  public svgStringToShape(svg: string): Shape {
    return this.MakerJSModel2Shape(
      MakerJS.model.mirror(
        MakerJS.model.scale(MakerJS.importer.fromSVGPathData(svg), 0.01),
        true,
        false,
      ),
    );
  }

  public pointsToShape(points: Array<[number, number]>): Shape {
    const shape = new Shape();
    let first = true;
    for (const pt of points) {
      if (first) {
        shape.moveTo(pt[0], pt[1]);
        first = false;
      } else {
        shape.lineTo(pt[0], pt[1]);
      }
    }
    return shape;
  }

  public shapeToMesh(
    length: number,
    material: Material,
    shape: Shape,
    steps = 1,
    vertexMod?: (geo: ExtrudeGeometry, length: number) => void,
  ): Mesh {
    const geo = new ExtrudeGeometry(shape, {
      bevelEnabled: false,
      depth: length,
      steps,
    });

    if (typeof vertexMod === "function") {
      vertexMod(geo, length);
    }

    const mesh = new Mesh(geo);
    this.trans3(mesh, [0, 0, -length / 2]);
    mesh.material = material;
    return mesh;
  }
}

export const ThreeToolsInstance = new ThreeTools();
export const D90 = ThreeToolsInstance.DEG2RAD(90);
export const D180 = ThreeToolsInstance.DEG2RAD(180);
